import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SessionRepository } from '@frontend-site/shared/core';
import { RdxProject, ProjectUser, SapCountry } from './model/rdx-project.model';
import { Iteration } from './model/iteration.model';
import { ApprovalProjectStepBeans } from './model/approval-project-step-beans.model';
import { ApprovalWorkflowTemplatesResponse } from './model/approval-workflow-templates-response.model';

const PROJECT_CONTEXT_TYPE = 1;

@Injectable({ providedIn: 'root' })
export class ProjectDetailsService {
    constructor(private http: HttpClient, private sessionRepository: SessionRepository) {}

    getProject(projectId: number): Observable<RdxProject> {
        const companyId = this.sessionRepository.applicationConfig?.companyId ?? 0;
        const params = new HttpParams()
            .set('companyId', companyId)
            .set('contextId', projectId)
            .set('contextType', PROJECT_CONTEXT_TYPE);
        return this.http.get<RdxProject>(`/rest/rdx/project/${projectId}`, { params });
    }

    saveProject(entity: RdxProject): Observable<RdxProject> {
        return this.http.post<RdxProject>('/rest/rdx/project', entity, { params: { projectId: entity.id } });
    }

    getProjectUsers(projectId: number): Observable<ProjectUser[]> {
        return this.http.get<ProjectUser[]>(`/rest/rdx/${projectId}/projectUsers`);
    }

    getSapCountries(projectId: number): Observable<SapCountry[]> {
        const params = new HttpParams().set('projectId', projectId);
        return this.http.get<SapCountry[]>('/rest/sap/sapCountries', { params });
    }

    getIterations(projectId: number): Observable<Iteration[]> {
        return this.http.get<Iteration[]>(`/rest/iteration/list/${projectId}`);
    }

    saveIteration(iteration: Partial<Iteration> & { projectId: number }): Observable<Iteration> {
        return this.http.post<Iteration>('/rest/iteration/save', iteration, { params: { projectId: iteration.projectId } });
    }

    deleteIterations(payload: { entitiesIds: number[]; projectId: number }): Observable<void> {
        return this.http.post<void>('/rest/iteration/delete', payload, { params: { projectId: payload.projectId } });
    }

    getApprovalSteps(projectId: number): Observable<ApprovalProjectStepBeans> {
        return this.http.get<{ projectSteps: any[]; templateName: string }>(
            `/rest/pqm/projectSetup/approvalWorkflow/getSteps/${projectId}`
        ).pipe(
            map(r => ({ projectSteps: r.projectSteps ?? [], name: r.templateName ?? '' }))
        );
    }

    getApprovalTemplates(projectId: number): Observable<ApprovalWorkflowTemplatesResponse> {
        const companyId = this.sessionRepository.applicationConfig?.companyId ?? 0;
        return this.http.get<ApprovalWorkflowTemplatesResponse>(
            `/rest/pqm/companySetup/approvalWorkflow/getTemplates/${companyId}/${projectId}`
        );
    }

    changeProjectApprovalTemplate(saveBean: { companyTemplateId: number; projectId: number }): Observable<unknown> {
        return this.http.post<unknown>('/rest/rdx/project/changeProjectApprovalTemplate', saveBean, { params: { projectId: saveBean.projectId } });
    }
}
